import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  ButtonGroup,
  closeDialog,
  Form,
  FormButton,
  FormField,
  openDialog,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceOrderJobQuery,
  usePutServiceOrderJobMechanicsMutation,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {ConfirmAssignMechanic} from './ConfirmAssignMechanic';

interface AssignMechanicFormProps extends TestIdProps {
  onClose: () => void;
  selectedMechanics: string[];
  orderId: string;
  serviceJobId: string;
  serviceCaseId: string;
  competenceId: string;
}

type FormType = {mechanics: string[]};

export function AssignMechanicForm(props: AssignMechanicFormProps) {
  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNil(props.serviceCaseId)}
  );
  const {data: employeeMechanics} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: serviceCase?.authorizationProfileId ?? '',
      isActive: true,
      withToOffer: true,
      employeeCompetenceId: isNotNilOrEmpty(props.competenceId) ? props.competenceId : undefined,
    },
    {
      skip:
        isNil(serviceCase?.authorizationProfileId) ||
        !isFeatureEnabled(featureFlags.ACL_EMPLOYEE_MANAGEMENT),
    }
  );
  const {data: job} = useGetServiceOrderJobQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.orderId,
    serviceJobId: props.serviceJobId,
  });
  const [putServiceOrderJobMechanics, {isLoading}] = usePutServiceOrderJobMechanicsMutation();

  const handleSubmit = async (data: FormType) => {
    if (job?.hasItems) {
      await openDialog(
        <ConfirmAssignMechanic
          onConfirm={() => {
            closeDialog('confirmAssignMechanic');
            props.onClose();
          }}
          onClose={() => closeDialog('confirmAssignMechanic')}
          mechanics={data.mechanics}
          employeeMechanics={employeeMechanics ?? []}
          orderId={props.orderId}
          serviceJobId={props.serviceJobId}
          serviceCaseId={props.serviceCaseId}
          data-testid={suffixTestId('confirmAssignMechanic', props)}
        />,
        {
          title: i18n.t('entity.orderRequest.labels.assignMechanic'),
          size: 'small',
          id: 'confirmAssignMechanic',
        }
      );
    } else {
      await putServiceOrderJobMechanics({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.orderId,
        serviceJobId: props.serviceJobId,
        body: {
          assignMechanics: data.mechanics?.map((mechanic, index) => ({
            id: mechanic,
            isDefault: index === 0,
            costCenterId: employeeMechanics?.find((employee) => employee?.employeeId === mechanic)
              ?.costCenterId,
          })),
          shouldUpdateJobItems: false,
        },
      })
        .unwrap()
        .then(() => props.onClose())
        .catch(handleApiError);
    }
  };

  const mechanicOptions =
    employeeMechanics?.map((mechanic) => ({
      label: mechanic?.name,
      value: mechanic?.employeeId,
    })) ?? [];

  return (
    <Form<FormType> onSubmit={handleSubmit} defaultValues={{mechanics: props.selectedMechanics}}>
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="mechanics"
            type="multiChoice"
            options={mechanicOptions}
            menuInPortal
            data-testid={suffixTestId('mechanics', props)}
            isNotClearable
          />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              onClick={props.onClose}
              variant="secondary"
              data-testid={suffixTestId('close', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              data-testid={suffixTestId('save', props)}
              isLoading={isLoading}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
